import * as Tooltip from "@radix-ui/react-tooltip";
import React from "react";
import { FormattedMessage } from "react-intl";
import { NavLink } from "react-router-dom";

import { isPowerCapStatus } from "~/common/courses";
import { getLastCorrection } from "~/common/patient";
import { PowerCapCourseTooltip } from "~/components/patient/patient_new/patient_new_course";
import { Button } from "~/components/ui/button";
import { LabelledValue } from "~/components/ui/labelled-value";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { TooltipRootClickable } from "~/components/ui/tooltip";
import type { TPatient } from "~/types/patient";

export function canShowPowerCapApplications(patient: TPatient): boolean {
  return Boolean(patient.power_cap_applications.length > 0 || isPowerCapStatus(patient.status));
}

export function canShowPowerCapCard(patient: TPatient): boolean {
  const { can_order_power_cap, power_cap_in_production } = getLastCorrection(patient).order_options;
  return Boolean(can_order_power_cap || power_cap_in_production || patient.power_cap_applications.length > 0 || isPowerCapStatus(patient.status));
}

export function PowerCapApplicationsList({ patient }: { patient: TPatient }) {
  return (
    <ul className="tw-m-0 tw-list-none tw-space-y-1 tw-p-0" data-testid="patient-power-cap-applications">
      {patient.power_cap_applications.map((application) => (
        <li key={application.patient_id}>
          <NavLink to={`/pages/patient/${application.patient_id}`} className="tw-underline tw-underline-offset-2">
            <FormattedMessage id="pat.power_cap.linked" values={{ id: application.patient_id }} />
          </NavLink>
          {application.status ? (
            <>
              {" — "}
              <FormattedMessage id={application.status} />
            </>
          ) : null}
          {application.ships_with_retainers ? (
            <>
              {" · "}
              <FormattedMessage id="pat.power_cap.ships_with_retainers" />
            </>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function PowerCapDetails({ patient }: { patient: TPatient }) {
  return (
    <div className="tw-space-y-1">
      {isPowerCapStatus(patient.status) ? (
        <p className="tw-m-0" data-testid="patient-power-cap-current-status">
          <FormattedMessage id={patient.status} />
        </p>
      ) : null}
      {isPowerCapStatus(patient.status) && patient.original_patient?.ships_with_retainers ? (
        <p className="tw-m-0" data-testid="patient-power-cap-ships-with-retainers">
          <FormattedMessage id="pat.power_cap.ships_with_retainers" />{" "}
          <NavLink to={`/pages/patient/${patient.original_patient.patient_id}`} className="tw-underline tw-underline-offset-2">
            <FormattedMessage id="pat.power_cap.linked" values={{ id: patient.original_patient.patient_id }} />
          </NavLink>
        </p>
      ) : null}
      {patient.power_cap_applications.length > 0 ? <PowerCapApplicationsList patient={patient} /> : null}
    </div>
  );
}

/** A cap in production blocks a new order; the disabled button explains why on hover or tap. */
function PowerCapInProductionButton() {
  return (
    <Tooltip.Provider delayDuration={0}>
      <TooltipRootClickable className="tw-block tw-w-full">
        <Tooltip.Trigger asChild>
          <Button type="button" aria-disabled className="tw-h-auto tw-w-full tw-cursor-not-allowed tw-opacity-[.65]" data-testid="power-cap-order-disabled">
            {/* Button forces nowrap; the label wraps in a narrow side column. */}
            <span className="tw-whitespace-normal">
              <FormattedMessage id="pat.status.button.power_cap" />
            </span>
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content className="tw-max-w-[280px] tw-rounded tw-bg-[#f7f7f7] tw-p-2.5 tw-text-xs tw-shadow-md" sideOffset={5} side="top">
            <FormattedMessage id="pat.power_cap.in_production_hint" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </TooltipRootClickable>
    </Tooltip.Provider>
  );
}

export function PatientPowerCapCard({ patient }: { patient: TPatient }) {
  const { can_order_power_cap, power_cap_in_production } = getLastCorrection(patient).order_options;

  return (
    <Portlet id="power-cap-side-block">
      <PortletTitle as="h2" iconClassName="icon-speech" id="power-cap-side-block-name">
        <FormattedMessage id="pat.power_cap.block.header" />
        <PowerCapCourseTooltip />
      </PortletTitle>

      <div className="portlet-body tw-flex tw-flex-col tw-items-start tw-gap-4">
        {can_order_power_cap ? (
          <Button className="tw-h-auto tw-w-full" asChild>
            <NavLink to={`/pages/patient/${patient.patient_id}/power-cap`}>
              {/* Button forces nowrap; the label wraps in a narrow side column. */}
              <span className="tw-whitespace-normal">
                <FormattedMessage id="pat.status.button.power_cap" />
              </span>
            </NavLink>
          </Button>
        ) : power_cap_in_production ? (
          <PowerCapInProductionButton />
        ) : null}

        {canShowPowerCapApplications(patient) ? (
          <div className="tw-w-full">
            <PowerCapDetails patient={patient} />
          </div>
        ) : null}
      </div>
    </Portlet>
  );
}

export function PatientPowerCapField({ patient }: { patient: TPatient }) {
  if (!canShowPowerCapApplications(patient)) {
    return null;
  }

  return (
    <div data-testid="patient-power-cap-field">
      <LabelledValue label={<FormattedMessage id="pat.power_cap.block.header" />}>
        <PowerCapDetails patient={patient} />
      </LabelledValue>
    </div>
  );
}
